import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { ReduxStates } from '../../redux/reduxTypes';

import { GraphTableData } from '../../types';
import { ReactComponent as TableArrowIcon } from '../../assets/icons/graph-table-arrow-icon.svg';
import { intToString, returnCurrencySymbol } from '../../helpers/format';
import { useEffect } from 'react';
import constants from '../../helpers/constants';
import DropDownFilter from '../DropDownFilter';
import ToolTip from '../Tooltip';

interface Props {
  timeRange?: string;
  data: Array<GraphTableData> | null;
  options: string[];
  onOptionChange?: (arg1: string) => void;
  selectedCurrency: string;
  unavailableData?: boolean;
  staticTime?: boolean | null;
  loading?: boolean | null;
  showTooltips?: boolean | null;
}
interface VariantStylesProps {
  [key: string]: {
    [key: string]: string;
  };
}

const variantStyles: VariantStylesProps = {
  sort: {
    high: 'transform rotate-180'
  },
  status: {
    drop: '!bg-[#ED2323]',
    growth: '!bg-[#009965]'
  }
};

const GraphTable = ({
  data,
  timeRange,
  onOptionChange,
  options,
  selectedCurrency,
  unavailableData,
  staticTime,
  loading,
  showTooltips
}: Props): React.ReactElement => {
  // TODO: use this sort boolean to proper sort values;
  const [sortedData, setSortedData] = useState<GraphTableData[] | null>(null);
  const [sortHigh, setSortHigh] = useState<boolean>(false);
  const [showRegions, setShowRegions] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [activeOptions, setActiveOptions] = useState<string[]>(
    options.filter((option) => option !== options[activeIndex])
  );
  const [primaryFilter, setPrimaryFilter] = useState<string>(options[activeIndex]);
  const [optionDataUnavailable, setOptionDataUnavailable] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const copyData = useSelector((state: ReduxStates) => state.copyData);
  const client = useSelector((state: ReduxStates) => state.client);

  useEffect(() => {
    if (options[activeIndex]) {
      setPrimaryFilter(options[activeIndex]);
      setActiveOptions(options.filter((option) => option !== options[activeIndex]));
      onOptionChange && onOptionChange(options[activeIndex]);
    } else {
      setPrimaryFilter(options[0]);
      setActiveOptions(options.filter((option) => option !== options[0]));
      onOptionChange && onOptionChange(options[0]);
      setActiveIndex(0);
    }
  }, [activeIndex, options]);

  useEffect(() => {
    if (data) {
      setOptionDataUnavailable(false);
      const hasDataValues = data?.find((a) => a);
      if (hasDataValues) {
        setSortedData(null);
        setSortHigh(false);
        data.sort((a, b) => b.currentValue - a.currentValue);
        setSortedData(data);
      } else {
        setSortedData(null);
        setOptionDataUnavailable(true);
      }
    } else {
      setSortedData(data);
    }
  }, [data]);

  useEffect(() => {
    if (typeof unavailableData !== 'undefined') setOptionDataUnavailable(unavailableData);
  }, [unavailableData]);

  useEffect(() => {
    if (typeof loading !== 'undefined' && loading !== null) {
      setIsLoading(loading);
    }
  }, [loading]);

  useEffect(() => {
    if (!sortedData) return;
    const sorted = sortHigh
      ? sortedData.sort((a, b) => a.currentValue - b.currentValue)
      : sortedData.sort((a, b) => b.currentValue - a.currentValue);

    setSortedData([...sorted]);
  }, [sortHigh]);

  const handlePercentage = (value: number) => {
    if ((!value && value !== 0) || value === Number.MAX_VALUE || !isFinite(value)) return '--';

    const indicator = value < 0 ? '-' : '';
    return indicator + Math.abs(value) + '%';
  };
  const handleValueORPercentage = (value: number, valueType: any) => {
    if ((!value && value !== 0) || value === Number.MAX_VALUE || !isFinite(value)) return '--';
    const isPercentage = valueType === 'percentage';
    const valueString = Math.abs(value).toString();
    const unit = isPercentage ? '%' : null;
    let withoutUnit: any = valueString;
    if (!isPercentage) {
      withoutUnit = unit
        ? value < 1
          ? valueString.substring(0, 4)
          : valueString.substring(0, 3)
        : intToString(Number(valueString), 1, false, selectedCurrency);
    }
    const withUnit = unit
      ? `${withoutUnit}${unit}`
      : valueType === 'currency'
      ? `${returnCurrencySymbol(selectedCurrency)}${withoutUnit}`
      : `${withoutUnit}`;

    const indicator = value < 0 ? '-' : '';

    return indicator + withUnit;
  };

  const mainHeaderStyles =
    'cursor-pointer relative text-left w-[15vw] lg:w-[20vw] box-border py-3 pb-[10px] font-nunitoRegular border-r border-borderGray text-sm';
  const mainHeaderTextStyles = 'w-[80%] inline-block font-nunitoLight';
  const secondHeaderStyles = 'text-center box-border p-3 w-[10vw] font-nunitoRegular text-xs font-normal';
  const dataRowStyles = 'font-nunitoRegular text-sm tracking-[0.2px] py-5';
  const statusStyles = 'mx-auto rounded-[2px] text-center py-[5px] px-0 w-[95%] block font-nunitoBold text-white';
  const baseIconStyles = '2lg:relative 2lg:right-[15px]';
  return (
    <div className="w-full bg-white rounded-sm">
      <table className={`w-full border-collapse table-fixed relative`}>
        <tbody>
          <tr className="border-borderGray shadow-tableHeaderShadow">
            <th
              className={`${mainHeaderStyles} ${staticTime ? 'w-[36vw]' : ''}`}
              onClick={() => setShowRegions(!showRegions)}
              colSpan={staticTime ? 2 : 1}
            >
              {options.length > 1 ? (
                <DropDownFilter
                  overrideStyles={{
                    border: '0px solid rgba(0, 59, 88, 0.21) !important'
                  }}
                  tableGraph
                  options={activeOptions}
                  onChange={(option) => {
                    const index: number = options.indexOf(option);
                    setActiveIndex(index);
                  }}
                  value={primaryFilter}
                  variant="default"
                />
              ) : (
                <span className="text-[14px] font-nunitoLight ml-3">{primaryFilter}</span>
              )}
            </th>
            {timeRange && (
              <th className={`${mainHeaderStyles} relative`} onClick={() => setSortHigh(!sortHigh)}>
                <div className="flex items-center justify-between px-3">
                  <span className={mainHeaderTextStyles}>{timeRange}</span>
                  <TableArrowIcon className={`${baseIconStyles} ${sortHigh && variantStyles.sort.high}`} />
                </div>
              </th>
            )}
            <th className={secondHeaderStyles}>{copyData?.grid_percent_previous_period}</th>
            <th className={secondHeaderStyles}>{copyData?.grid_change_previous_period}</th>
            <th className={secondHeaderStyles}>{copyData?.grid_percent_last_year}</th>
            <th className={secondHeaderStyles}>{copyData?.grid_change_last_year}</th>
          </tr>
          {sortedData &&
            sortedData.length > 0 &&
            data &&
            sortedData?.map((sortedData, index) => {
              if (client === 'demo' && sortedData.dimension === 'Spend As % Of Gross Revenue') {
                sortedData.currentValue = 6.91;
                sortedData.lastYearChangePercentage = 12.83;
                sortedData.lastYearChangeValue = 0.75;
                sortedData.previousPeriodChangePercentage = -1.39;
                sortedData.previousPeriodChangeValue = -0.1;
                sortedData.currentValueType = 'percentage';
                sortedData.dataStatus = {
                  status: 'PARTIAL',
                  label: '6/13',
                  message: 'Calculation is using 6 of 13 markets: IT, DE, US, BR, AE, ES'
                };
              }
              return (
                <tr className="border-b border-borderGray box-border" key={index}>
                  <td className={`${dataRowStyles} pl-[15px]`} style={{ position: 'relative' }}>
                    {sortedData.dataStatus && showTooltips ? (
                      <div className="relative">
                        <span className="inline pr-1">{sortedData.dimension}</span>
                        <ToolTip data={sortedData.dataStatus} />
                      </div>
                    ) : (
                      sortedData.dimension
                    )}
                    {
                      <h6 className="font-nunitoBold text-[22px] m-0 absolute top-1/2 right-[20%] leading-[33px] transform -translate-y-1/2">
                        {sortedData.untitledNumber && sortedData.untitledNumber}
                        {sortedData.untitledNumberUnit && sortedData.untitledNumberUnit}
                      </h6>
                    }
                  </td>
                  {timeRange && (
                    <td className={dataRowStyles} onClick={() => setSortHigh(!sortHigh)}>
                      <div className="font-nunitoBold text-[22px] pl-4">
                        {handleValueORPercentage(sortedData.currentValue, sortedData.currentValueType)}
                      </div>
                    </td>
                  )}
                  {staticTime && (
                    <td className={dataRowStyles}>
                      <div className="font-nunitoBold text-[22px] pl-4">
                        {handleValueORPercentage(sortedData.currentValue, sortedData.currentValueType)}
                      </div>
                    </td>
                  )}
                  <td className={dataRowStyles}>
                    <div
                      className={`${statusStyles} ${
                        sortedData.previousPeriodChangePercentage < 0
                          ? variantStyles.status.drop
                          : variantStyles.status.growth
                      }`}
                    >
                      {handlePercentage(sortedData.previousPeriodChangePercentage)}
                    </div>
                  </td>
                  <td className={dataRowStyles}>
                    <div
                      className={`${statusStyles} ${
                        sortedData.previousPeriodChangeValue < 0
                          ? variantStyles.status.drop
                          : variantStyles.status.growth
                      }`}
                    >
                      {handleValueORPercentage(sortedData.previousPeriodChangeValue, sortedData.currentValueType)}
                    </div>
                  </td>
                  <td className={dataRowStyles}>
                    <div
                      className={`${statusStyles} ${
                        sortedData.lastYearChangePercentage < 0
                          ? variantStyles.status.drop
                          : variantStyles.status.growth
                      }`}
                    >
                      {handlePercentage(sortedData.lastYearChangePercentage)}
                    </div>
                  </td>
                  <td className={dataRowStyles}>
                    <div
                      className={`${statusStyles} ${
                        sortedData.lastYearChangeValue < 0 ? variantStyles.status.drop : variantStyles.status.growth
                      }`}
                    >
                      {handleValueORPercentage(sortedData.lastYearChangeValue, sortedData.currentValueType)}
                    </div>
                  </td>
                </tr>
              );
            })}

          {isLoading && (
            <tr>
              <td colSpan={6} className="py-[50px]">
                <p className={constants.noData}>{copyData?.loading}</p>
              </td>
            </tr>
          )}

          {optionDataUnavailable && !isLoading && (
            <tr>
              <td colSpan={6} className="py-[50px]">
                <p className={constants.noData}>{copyData?.no_data}</p>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default GraphTable;
